// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Pinia store for tenant management against the system-admin BFF (#254).
 * Operations require an Auth0 access_token with `read:all_tenants` /
 * `write:all_tenants` scopes; the SPA itself does not handle IAM
 * credentials.
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useApiClient, getBffBaseUrl } from '../composables/useApiClient';

export const useTenantStore = defineStore('tenant', () => {
  const tenants = ref([]);
  const selectedTenant = ref(null);
  const loading = ref(false);
  const nextToken = ref(null);
  const statusFilter = ref('all');

  const apiClient = useApiClient();

  const activeTenants = computed(() =>
    tenants.value.filter((t) => t.status === 'active')
  );

  const selectedEventId = computed(() =>
    selectedTenant.value?.event_id || ''
  );

  /**
   * GET /tenants?status=...&next_token=... via the system-admin BFF.
   * Silently no-ops if the BFF URL is not configured (local dev).
   */
  async function fetchTenants() {
    const baseUrl = getBffBaseUrl();
    if (!baseUrl) {
      return;
    }

    loading.value = true;
    try {
      const params = new URLSearchParams();
      if (statusFilter.value && statusFilter.value !== 'all') {
        params.set('status', statusFilter.value);
      }
      if (nextToken.value) {
        params.set('next_token', nextToken.value);
      }
      const queryString = params.toString();
      const url = `${baseUrl}/tenants${queryString ? '?' + queryString : ''}`;

      const response = await apiClient.apiGet(url);
      const data = await response.json();
      tenants.value = data.tenants || [];
      nextToken.value = data.next_token || null;
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    } finally {
      loading.value = false;
    }
  }

  async function createTenant(tenantData) {
    const baseUrl = getBffBaseUrl();
    const url = `${baseUrl}/tenants`;
    const response = await apiClient.apiPost(url, tenantData);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to create tenant: ${response.status}`);
    }
    await fetchTenants();
  }

  async function updateTenant(eventId, updates) {
    const baseUrl = getBffBaseUrl();
    const url = `${baseUrl}/tenants/${encodeURIComponent(eventId)}`;
    const response = await apiClient.apiPut(url, updates);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update tenant: ${response.status}`);
    }
    await fetchTenants();
  }

  async function setTenantStatus(eventId, status) {
    const baseUrl = getBffBaseUrl();
    const url = `${baseUrl}/tenants/${encodeURIComponent(eventId)}/status`;
    const response = await apiClient.apiPatch(url, { status });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to set tenant status: ${response.status}`);
    }
    await fetchTenants();
  }

  async function deleteTenant(eventId) {
    const baseUrl = getBffBaseUrl();
    const url = `${baseUrl}/tenants/${encodeURIComponent(eventId)}`;
    const response = await apiClient.apiDelete(url);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to delete tenant: ${response.status}`);
    }
    if (selectedTenant.value && selectedTenant.value.event_id === eventId) {
      selectedTenant.value = null;
    }
    await fetchTenants();
  }

  /**
   * Select a tenant.
   */
  function selectTenant(tenant) {
    selectedTenant.value = tenant;
  }

  function setStatusFilter(filter) {
    statusFilter.value = filter;
  }

  return {
    tenants,
    selectedTenant,
    loading,
    nextToken,
    statusFilter,
    activeTenants,
    selectedEventId,
    fetchTenants,
    createTenant,
    updateTenant,
    setTenantStatus,
    deleteTenant,
    selectTenant,
    setStatusFilter,
  };
});
